import type { ReactElement, ReactNode } from "react";
import { Route } from "react-router-dom";
import type { IRoute } from "@/types/interfaces";
import HomeContainer from "@/components/containers/homeContainer";
import Home from "@/pages";
import RequestQuoteComposePage from "@/pages/hire/request-quote-compose/index";
import RequestQuoteReviewPage from "@/pages/hire/request-quote-review/index";
import ServicesPage from "@/pages/services";
import TermsOfUse from "@/pages/termOfUse";
import TextMessagePolicy from "@/pages/textMessagePolicy";
import NotFoundPage from "@/pages/404";
import { UpdateWorkerProfile } from "@/pages/others/register/UpdateWorkerProfile";
import MyProfilePage from "@/pages/my-profile";
import WorkerServiceDetails from "@/pages/worker/service";
import ShellContainer from "@/components/containers/shellContainer";
import MyProposals from "@/pages/shell/clientManagement/my-proposals/index";
import RequestQuotesPage from "@/pages/shell/clientManagement/request-quotes/index";
import RequestQuoteProposalsPage from "@/pages/shell/clientManagement/request-quote-proposals/index";
import MyJobs from "@/pages/shell/workerManagement/my-jobs";
import ProviderProposalsPage from "@/pages/shell/workerManagement/provider-proposals/index";
import ProviderRequestQuotesPage from "@/pages/shell/workerManagement/provider-request-quotes/index";
import ProviderRequestQuoteDetailPage from "@/pages/shell/workerManagement/provider-request-quotes/detail";
import ChatUI from "@/pages/shell/messages/ChatUI";
import TransactionsList from "@/pages/shell/workerManagement/transactions-list";
import ClientTransactionsList from "@/pages/shell/clientManagement/transactions-list";
import UpdateCertifications from "@/pages/shell/workerManagement/cv-manager";
import SettingsPage from "@/pages/shell/settings";
import MyVouchersPage from "@/pages/shell/my-vouchers";
import PrivacyPolicy from "@/pages/privacyPolicy";
import ServiceDetailPage from "@/pages/services/serviceDetail";
import ClientGuidanceTabs from "@/pages/shell/clientManagement/clientFaq";
import ProviderGuidanceTabs from "@/pages/shell/workerManagement/providerFaq";
import HelpPage from "@/pages/shell/help";
import DisputesList from "@/pages/shell/disputes";
import CompleteEssentialInfo from "@/pages/auth/complete-essential-info";
import SignupPage from "@/pages/auth/signup";
import SignupWorkerPage from "@/pages/auth/signup/worker";
import SignupCustomerPage from "@/pages/auth/signup/customer";
import LoginPage from "@/pages/auth/login/LoginPage";
import ForgotPasswordPage from "@/pages/auth/forgotPassword/ForgotPasswordPage";
import FAQs from "@/pages/faqs";
import WhatMovesUs from "@/pages/whatMovesUs";
import {
  AuthPrivateRoutes,
  WorkerPrivateRoutes,
  ClientPrivateRoutes,
} from "@/router/PrivateRoutes";

export type PublicRouteOverrides = {
  /** Vike SSR: inject server-fetched homepage workers listing */
  home?: ReactElement;
  /** Vike SSR: inject server-fetched services listing */
  servicesList?: ReactElement;
  /** Vike SSR: inject server-fetched service page */
  serviceDetail?: ReactElement;
  /** Vike SSR: inject server-fetched worker profile page */
  workerDetail?: ReactElement;
  faqa?: ReactElement;
  whatMovesUs?: ReactElement;
  termOfUse?: ReactElement;
  privacyPolicy?: ReactElement;
  textMessagePolicy?: ReactElement;
  login?: ReactElement;
  signup?: ReactElement;
};

export function createPublicRoutes(overrides: PublicRouteOverrides = {}): IRoute[] {
  return [
    {
      link: "/",
      element: <HomeContainer />,
      children: [
        { link: "", element: overrides.home ?? <Home /> },
        { link: "request-quote", element: <RequestQuoteComposePage /> },
        { link: "request-quote/review", element: <RequestQuoteReviewPage /> },
        {
          link: "profesional/:slug",
          element: overrides.workerDetail ?? <WorkerServiceDetails />,
        },
        { link: "verify-account", element: <Home /> },
        { link: "reset-password", element: <Home /> },
        { link: "update-worker", element: <UpdateWorkerProfile /> },
        { link: "complete-essential-info", element: <CompleteEssentialInfo /> },
        { link: "registro", element: overrides.signup ?? <SignupPage /> },
        { link: "registro/worker", element: <SignupWorkerPage /> },
        { link: "registro/customer", element: <SignupCustomerPage /> },
        { link: "ingresar", element: overrides.login ?? <LoginPage /> },
        { link: "forgot-password", element: <ForgotPasswordPage /> },
        { link: "servicios", element: overrides.servicesList ?? <ServicesPage /> },
        { link: "preguntas-frecuentes", element: overrides.faqa ?? <FAQs /> },
        { link: "sobre-nosotros", element: overrides.whatMovesUs ?? <WhatMovesUs /> },
        {
          link: "service/:slug",
          element: overrides.serviceDetail ?? <ServiceDetailPage />,
        },
        { link: "terminos-de-uso", element: overrides.termOfUse ?? <TermsOfUse /> },
        { link: "politica-de-privacidad", element: overrides.privacyPolicy ?? <PrivacyPolicy /> },
        {
          link: "politica-de-mensajes",
          element: overrides.textMessagePolicy ?? <TextMessagePolicy />,
        },
      ],
    },
  ];
}

/** Default public routes for the CSR `App` shell (no Vike SSR props). */
export const DEFAULT_PUBLIC_ROUTES = createPublicRoutes();

export const workerShellRoutes: IRoute[] = [
  {
    link: "/",
    element: <ShellContainer />,
    protectedPath: false,
    children: [
      { link: "my-jobs", element: <MyJobs /> },
      { link: "provider-request-quotes", element: <ProviderRequestQuotesPage /> },
      {
        link: "provider-request-quotes/:requestQuoteJobId",
        element: <ProviderRequestQuoteDetailPage />,
      },
      { link: "provider-proposals", element: <ProviderProposalsPage /> },
      { link: "payments-history", element: <TransactionsList /> },
    ],
  },
];

export const clientShellRoutes: IRoute[] = [
  {
    link: "/",
    element: <ShellContainer />,
    protectedPath: false,
    children: [
      { link: "my-proposals", element: <MyProposals /> },
      { link: "request-quotes", element: <RequestQuotesPage /> },
      {
        link: "request-quotes/:requestQuoteJobId",
        element: <RequestQuoteProposalsPage />,
      },
      { link: "transactions-history", element: <ClientTransactionsList /> },
      { link: "client-faq", element: <ClientGuidanceTabs /> },
    ],
  },
];

export const authShellRoutes: IRoute[] = [
  {
    link: "/",
    element: <ShellContainer />,
    protectedPath: false,
    children: [
      { link: "settings", element: <SettingsPage /> },
      { link: "my-vouchers", element: <MyVouchersPage /> },
      { link: "chat", element: <ChatUI /> },
      { link: "chat/:chatId", element: <ChatUI /> },
      { link: "my-profile", element: <MyProfilePage /> },
      { link: "update-certifications", element: <UpdateCertifications /> },
      { link: "provider-faq", element: <ProviderGuidanceTabs /> },
      { link: "disputes", element: <DisputesList /> },
      { link: "help", element: <HelpPage /> },
    ],
  },
];

function mapNestedRoutes(routes: IRoute[]) {
  return routes.map(({ link, element, children }) => (
    <Route key={String(link)} path={link} element={element}>
      {children?.map((child) => (
        <Route key={String(child.link)} path={child.link} element={child.element} />
      ))}
    </Route>
  ));
}

export type RenderAppMainRoutesOptions = {
  /** When set (e.g. Vike SSR pages), overrides default `ServiceDetailPage` / `WorkerServiceDetails` elements. */
  publicRoutes?: IRoute[];
};

/**
 * Returns the same route tree as `App` main `<Routes>` children (public + auth shell + 404).
 * Must be invoked as `{renderAppMainRoutes()}` inside `<Routes>` — not `<AppMainRoutes />` —
 * because React Router only accepts `<Route>` or `<Fragment>` as direct `Routes` children.
 */
export function renderAppMainRoutes(
  options: RenderAppMainRoutesOptions = {}
): ReactNode {
  const publicRoutes = options.publicRoutes ?? DEFAULT_PUBLIC_ROUTES;

  return (
    <>
      {mapNestedRoutes(publicRoutes)}

      <Route element={<AuthPrivateRoutes />}>
        <Route element={<ClientPrivateRoutes />}>
          {mapNestedRoutes(clientShellRoutes)}
        </Route>

        <Route element={<WorkerPrivateRoutes />}>
          {mapNestedRoutes(workerShellRoutes)}
        </Route>

        {mapNestedRoutes(authShellRoutes)}
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </>
  );
}
